import React from 'react'
import { X, User, Briefcase, Phone, CreditCard, MapPin, Calendar, Mail, Shield, Building, DollarSign, Clock, UserCheck } from 'lucide-react'

const ViewEmployeeModal = ({ isOpen, onClose, employee }) => {
  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = 'N/A') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return 'N/A'
    }
  }

  if (!isOpen || !employee) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">
                {employee.fullName} - {employee.employeeId || employee._id?.slice(-8) || 'N/A'}
              </h2>
              <p className="text-orange-100">Employee Details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col max-h-[calc(90vh-140px)]">
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {/* Personal Information Section */}
              <div className="mb-8">
                <div className="flex items-center space-x-2 mb-4">
                  <User className="h-5 w-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                    <p className="text-gray-900 font-medium">{employee.fullName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Contact Number</label>
                    <p className="text-gray-900">{employee.contactNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                    <p className="text-gray-900">{employee.emailAddress || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Date of Birth</label>
                    <p className="text-gray-900">{formatDate(employee.dateOfBirth)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Gender</label>
                    <p className="text-gray-900 capitalize">{employee.gender || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">National ID</label>
                    <p className="text-gray-900">{employee.nicPassportDrivingLicense || 'N/A'}</p>
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
                    <p className="text-gray-900">{employee.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Job Information Section */}
              <div className="mb-8">
                <div className="flex items-center space-x-2 mb-4">
                  <Briefcase className="h-5 w-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Job Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Job Title</label>
                    <p className="text-gray-900 font-medium">{safeGet(employee, 'job.name')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Branch</label>
                    <p className="text-gray-900">{safeGet(employee, 'branch.name')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Join Date</label>
                    <p className="text-gray-900">{formatDate(employee.dateOfJoining)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Employment Type</label>
                    <p className="text-gray-900 capitalize">{employee.employeeType || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Basic Salary</label>
                    <p className="text-gray-900 font-semibold">
                      {employee.basicSalary ? `Rs ${employee.basicSalary.toLocaleString()}` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Service Charge Rate</label>
                    <p className="text-gray-900 font-semibold">
                      {employee.serviceChargeRate !== undefined && employee.serviceChargeRate !== null ? `${employee.serviceChargeRate}%` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Available Leaves</label>
                    <p className="text-gray-900">{employee.noOfLeaves || 0} days</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        employee.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {employee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Emergency Contact Section */}
              {(safeGet(employee, 'emergencyContact.name') !== 'N/A' || 
                safeGet(employee, 'emergencyContact.contactNumber') !== 'N/A') && (
                <div className="mb-8">
                  <div className="flex items-center space-x-2 mb-4">
                    <Phone className="h-5 w-5 text-orange-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Emergency Contact</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Contact Name</label>
                      <p className="text-gray-900">{safeGet(employee, 'emergencyContact.name')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Relationship</label>
                      <p className="text-gray-900">{safeGet(employee, 'emergencyContact.relationship')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Contact Number</label>
                      <p className="text-gray-900">{safeGet(employee, 'emergencyContact.contactNumber')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Account Section */}
              {(safeGet(employee, 'bankAccount.accountNumber') !== 'N/A' || 
                safeGet(employee, 'bankAccount.bankName') !== 'N/A') && (
                <div className="mb-8">
                  <div className="flex items-center space-x-2 mb-4">
                    <CreditCard className="h-5 w-5 text-orange-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Bank Account Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Account Holder Name</label>
                      <p className="text-gray-900">{safeGet(employee, 'bankAccount.accountName')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Account Number</label>
                      <p className="text-gray-900 font-mono">{safeGet(employee, 'bankAccount.accountNumber')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Bank Name</label>
                      <p className="text-gray-900">{safeGet(employee, 'bankAccount.bankName')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Branch Name</label>
                      <p className="text-gray-900">{safeGet(employee, 'bankAccount.bankBranch')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* System Information Section */}
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Shield className="h-5 w-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-gray-900">System Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
                    <p className="text-gray-900">{formatDate(employee.createdAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Updated Date</label>
                    <p className="text-gray-900">{formatDate(employee.updatedAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Created By</label>
                    <p className="text-gray-900">{safeGet(employee, 'createdBy.username') || safeGet(employee, 'createdBy.name') || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end px-6 pt-3 pb-3 border-t bg-gray-50 min-h-[60px] items-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewEmployeeModal
